/**
 * Unsaved-changes guard for draft clinical forms (ASM-03, Phase 4).
 *
 * - Browser level: while the form is dirty, a `beforeunload` listener asks the browser to show its
 *   "Leave site?" prompt on reload, tab close or typing a new URL.
 * - In-app: the app uses `<BrowserRouter>` (not a data router), so react-router's `useBlocker`
 *   is not available. Patient sections switch views with component state rather than routes, so
 *   callers wrap their own exits (Close, Back, list navigation) in `confirmNavigation(proceed)`,
 *   which opens a ConfirmDialog when there are unsaved changes.
 *
 * Limitation: navigation the form does not own (sidebar links, switching patient, browser Back)
 * is only covered by `beforeunload` when it unloads the page. Move to a data router
 * (createBrowserRouter + useBlocker) to block those as well.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export const UNSAVED_CHANGES_MESSAGE = 'You have unsaved changes. Leave without saving?';

export interface UnsavedChangesDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  destructive: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export interface UseUnsavedChangesGuardResult {
  /** Runs `proceed` now when clean, otherwise after the user confirms in the dialog */
  confirmNavigation: (proceed: () => void) => void;
  /** Spread onto <ConfirmDialog /> */
  dialogProps: UnsavedChangesDialogProps;
}

const readDirty = (isDirty: boolean | (() => boolean)): boolean =>
  typeof isDirty === 'function' ? isDirty() : isDirty;

/** Every mounted guard's dirty check, so app-level code can tell whether any form has unsaved work */
const dirtyReaders = new Set<() => boolean>();

/**
 * True while any mounted form that uses this guard has unsaved changes. The silent app updater
 * (components/offline/AppUpdater) never reloads the page while this is true.
 */
export function hasUnsavedChanges(): boolean {
  for (const read of dirtyReaders) {
    try {
      if (read()) return true;
    } catch {
      return true; // a broken reader counts as dirty: never risk losing work
    }
  }
  return false;
}

export function useUnsavedChangesGuard(
  isDirty: boolean | (() => boolean),
  message: string = UNSAVED_CHANGES_MESSAGE
): UseUnsavedChangesGuardResult {
  const dirtyRef = useRef(isDirty);
  dirtyRef.current = isDirty;
  const pendingRef = useRef<(() => void) | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!readDirty(dirtyRef.current)) return;
      event.preventDefault();
      // Required by Chrome/Edge to show the prompt
      event.returnValue = '';
    };
    const reader = () => readDirty(dirtyRef.current);
    dirtyReaders.add(reader);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      dirtyReaders.delete(reader);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  const confirmNavigation = useCallback((proceed: () => void) => {
    if (!readDirty(dirtyRef.current)) {
      proceed();
      return;
    }
    pendingRef.current = proceed;
    setOpen(true);
  }, []);

  const onConfirm = useCallback(() => {
    const proceed = pendingRef.current;
    pendingRef.current = null;
    setOpen(false);
    proceed?.();
  }, []);

  const onCancel = useCallback(() => {
    pendingRef.current = null;
    setOpen(false);
  }, []);

  return {
    confirmNavigation,
    dialogProps: {
      open,
      title: 'Unsaved changes',
      message,
      confirmLabel: 'Leave without saving',
      cancelLabel: 'Stay',
      destructive: true,
      onConfirm,
      onCancel,
    },
  };
}

export default useUnsavedChangesGuard;
